import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";
import { FileSearch, Quote, AlignLeft, MessageSquareText, FileCheck, FileText, ArrowRight, Sparkles } from "lucide-react";

const TOOLS = [
  { id: "plagiarism-checker", label: "Plagiarism Checker", icon: FileSearch, desc: "Check your text for originality and potential plagiarism indicators.", color: "#006039", bg: "var(--rolex-green-light)" },
  { id: "caption-craft", label: "CaptionCraft", icon: MessageSquareText, desc: "Generate platform-optimized social media captions in any tone.", color: "#8B3A4A", bg: "#FBF0F0" },
  { id: "summarizer", label: "Summarizer", icon: AlignLeft, desc: "Summarize text or documents into concise key points. Upload PDF/DOCX or paste.", color: "#5B8A72", bg: "#E8F2EC" },
  { id: "citation-generator", label: "Citation Generator", icon: Quote, desc: "Generate APA, MLA, Chicago, Harvard, and IEEE citations instantly.", color: "#1A3A5C", bg: "var(--info-bg)" },
  { id: "resume-checker", label: "AI Resume Checker", icon: FileCheck, desc: "ATS compatibility analysis with actionable path to 90%+ score.", color: "#5B8A72", bg: "#E8F2EC" },
  { id: "cover-letter", label: "Cover Letter Builder", icon: FileText, desc: "AI-crafted cover letters that sound human and land interviews.", color: "#006039", bg: "var(--rolex-green-light)", isNew: true },
];

export default function AIToolsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      {user ? <Navbar /> : (
        <nav className="sticky top-0 z-50" style={{ background: "rgba(250,250,248,0.8)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(232,228,220,0.6)" }}>
          <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
            <h1 className="font-display text-3xl font-bold cursor-pointer" style={{ color: "var(--rolex-green)" }} onClick={() => navigate("/")}>Buddy</h1>
            <button onClick={() => navigate("/login")} className="btn-primary py-2 px-5 text-sm">Sign In</button>
          </div>
        </nav>
      )}

      <main className="max-w-5xl mx-auto px-4 pt-10 pb-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4" style={{ background: "rgba(0,96,57,0.08)", color: "var(--rolex-green)" }}>
            <Sparkles size={14} /><span className="text-xs font-semibold tracking-wider uppercase">AI-Powered Tools</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold" style={{ color: "var(--text-primary)" }}>AI Tools by Buddy</h1>
          <p className="mt-2 text-base" style={{ color: "var(--text-muted)" }}>Professional writing, analysis, and productivity tools</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOOLS.map((tool, i) => (
            <motion.div key={tool.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="rounded-2xl p-6 cursor-pointer group relative" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}
              onClick={() => navigate(`/ai-tools/${tool.id}`)} whileHover={{ y: -6, boxShadow: "0 12px 32px rgba(0,0,0,0.08)" }} data-testid={`tool-card-${tool.id}`}>
              {tool.isNew && <span className="absolute -top-2 right-4 text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--rolex-green)", color: "white" }}>NEW</span>}
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: tool.bg }}>
                <tool.icon size={22} style={{ color: tool.color }} />
              </div>
              <h3 className="text-base font-semibold mb-2" style={{ color: "var(--text-primary)" }}>{tool.label}</h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--text-muted)" }}>{tool.desc}</p>
              <span className="text-xs font-medium flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: tool.color }}>Open Tool <ArrowRight size={13} /></span>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
